import { useEffect, useState, useRef } from 'react';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import { useLoaderData, useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { trackEvent } from 'lib/amplitude';
import ModeBar, { ModeBarIcon } from 'components/ModeBar';

const Page = styled.div`
  display: flex;
  flex-grow: 1;
`;

const DashboardWrapper = styled.div`
  flex-grow: 1;
  min-width: 800px;
  max-width: 1600px;
`;

/**
 * The top-level component for the application
 *
 * @returns {JSX.Element} - the dashboard
 */
export default function Dashboard() {
  const [embeddingContext, setEmbeddingContext] = useState(null);
  const [embeddedDashboard, setEmbeddedDashboard] = useState(null);
  const dashboardRef = useRef(null);

  const {
    embed_url: url,
    creation_date: run_date,
    input_layer_name: layer_name,
    dashboard_name: dashboardName,
    project_key: projectKey,
    project_type: projectType,
  } = useLoaderData();

  const { dashboardId, orgKey } = useParams();

  const exploreURL = `${import.meta.env.UF_APP_ORIGIN}/explore/${orgKey}/project/${projectKey}`;

  const handleMessage = (msg) => {
    if (msg.eventName === 'SELECTED_SHEET_CHANGED') {
      trackEvent('Sheet changed', {
        orgKey,
        projectKey,
        dashboardName,
        sheetName: msg.message.selectedSheet.Name,
      });
    }
  };

  const contentOptions = {
    onMessage: handleMessage,
    parameters: [
      {
        Name: 'LayerName',
        Values: [layer_name],
      },
      {
        Name: 'RunDate',
        Values: [run_date],
      },
    ],
    toolbarOptions: {
      export: true,
    },
  };

  const createContext = async () => {
    const context = await createEmbeddingContext();
    setEmbeddingContext(context);
  };

  const embed = async () => {
    const frameOptions = {
      url,
      container: dashboardRef.current,
      withIframePlaceholder: true,
    };

    const embedded = await embeddingContext.embedDashboard(
      frameOptions,
      contentOptions,
    );

    setEmbeddedDashboard(embedded);
  };

  /**
   * A note about the exhaustive-deps rule:
   *
   * AWS Quicksight is not a serious product, and its methods need to be called
   * in very specific, fussy ways that don't play nicely with React's hooks. If
   * we include all the actual dependencies for each effect, we end up with a
   * lot of calls to the Quicksight API that it doesn't expect, doing things
   * like destroying our embed context or invalidating the embed URL.
   *
   * To accommodate this obviously-not-ready-for-production API, we'll disable
   * the deps rule for the embed and navigation calls.
   *
   * !!! DANGER ZONE BELOW !!!
   */

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (url) {
      createContext();
    }
  }, []);

  useEffect(() => {
    if (embeddingContext) {
      embed();
    }
  }, [embeddingContext]);

  useEffect(() => {
    if (embeddedDashboard) {
      embeddedDashboard.navigateToDashboard(dashboardId, contentOptions);
    }
  }, [dashboardId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <Page>
      <ModeBar
        hasProject={!!projectKey}
        onClickMap={(e) => {
          e.preventDefault();
          trackEvent('Explore clicked', {
            orgKey,
            projectKey,
            dashboardName,
            dashboardId,
          });
          window.location.assign(exploreURL);
        }}
        styles={projectType?.style}
      >
        <ModeBarIcon
          src={projectType?.icon_url}
          label={projectType?.key}
          onClick={(e) => {
            e.preventDefault();
            trackEvent('Explore clicked', {
              orgKey,
              projectKey,
              dashboardName,
              dashboardId,
            });
            window.location.assign(`${exploreURL}?maponly=1`);
          }}
        />
      </ModeBar>
      <DashboardWrapper ref={dashboardRef} data-testid="dashboard" />
    </Page>
  );
}
